import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Board from "../components/Board";
import api from "../lib/api";
import { initialBoard, applyMove } from "../game/engine";
import { ChevronLeft, ChevronRight, SkipBack, SkipForward, Play, Pause, Loader2 } from "lucide-react";

export default function Replay() {
  const { gameId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [idx, setIdx] = useState(0); // 0 = initial board, N = after Nth move
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    api.get(`/games/replay/${gameId}`).then((r) => setData(r.data)).catch((e) => {
      setError(e.response?.data?.detail || "Partie introuvable");
    });
  }, [gameId]);

  // Auto-play
  useEffect(() => {
    if (!playing || !data) return;
    if (idx >= (data.moves?.length || 0)) {
      setPlaying(false);
      return;
    }
    const id = setTimeout(() => setIdx((i) => i + 1), 1100);
    return () => clearTimeout(id);
  }, [playing, idx, data]);

  const totalMoves = data?.moves?.length || 0;

  // Compute board state at idx by replaying moves 0..idx-1
  const { board, lastMove } = useMemo(() => {
    let b = initialBoard();
    let lm = null;
    if (data?.moves) {
      for (let i = 0; i < idx; i++) {
        const mv = data.moves[i];
        const move = { from: mv.from, to: mv.to, captures: mv.captures || [] };
        b = applyMove(b, move);
        lm = move;
      }
    }
    return { board: b, lastMove: lm };
  }, [data, idx]);

  if (error) {
    return (
      <div className="min-h-screen bg-tribal">
        <Navbar />
        <main className="max-w-3xl mx-auto px-6 py-16 text-center fade-up">
          <p className="text-rose-400 mb-4" data-testid="replay-error">{error}</p>
          <Link to="/profile" className="text-sm text-[color:var(--text-secondary)] hover:text-[color:var(--text-primary)]">← Retour au profil</Link>
        </main>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-tribal">
        <Navbar />
        <main className="max-w-3xl mx-auto px-6 py-16 text-center text-sm text-[color:var(--text-secondary)] flex items-center justify-center gap-2 fade-up">
          <Loader2 className="animate-spin" size={16} /> Chargement de la partie…
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-tribal">
      <Navbar />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 fade-up">
        <p className="text-xs tracking-[0.3em] text-[color:var(--primary)] mb-2">REPLAY</p>
        <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight mb-1" data-testid="replay-title">
          {data.player_a} <span className="text-[color:var(--text-secondary)]">vs</span> {data.player_b}
        </h1>
        <p className="text-xs text-[color:var(--text-secondary)] mb-6">
          {data.winner ? (
            <>Vainqueur : <span className="text-[color:var(--primary)] font-semibold">{data.winner === "A" ? data.player_a : data.player_b}</span> · {totalMoves} coups</>
          ) : (
            <>Égalité · {totalMoves} coups</>
          )}
        </p>

        <Board board={board} lastMove={lastMove} />

        {/* Controls */}
        <div className="max-w-[560px] mx-auto mt-5 glass rounded-lg p-4">
          <div className="flex items-center justify-center gap-2 mb-3">
            <button onClick={() => { setPlaying(false); setIdx(0); }} className="p-2 rounded-md border border-[color:var(--border)] hover:bg-[color:var(--surface)]" data-testid="replay-first">
              <SkipBack size={16} />
            </button>
            <button onClick={() => { setPlaying(false); setIdx((i) => Math.max(0, i - 1)); }} className="p-2 rounded-md border border-[color:var(--border)] hover:bg-[color:var(--surface)]" data-testid="replay-prev">
              <ChevronLeft size={16} />
            </button>
            <button onClick={() => setPlaying((p) => !p)} className="px-4 py-2 rounded-md btn-gold flex items-center gap-2 text-sm" data-testid="replay-playpause">
              {playing ? <Pause size={14} /> : <Play size={14} />}
              {playing ? "Pause" : "Lecture"}
            </button>
            <button onClick={() => { setPlaying(false); setIdx((i) => Math.min(totalMoves, i + 1)); }} className="p-2 rounded-md border border-[color:var(--border)] hover:bg-[color:var(--surface)]" data-testid="replay-next">
              <ChevronRight size={16} />
            </button>
            <button onClick={() => { setPlaying(false); setIdx(totalMoves); }} className="p-2 rounded-md border border-[color:var(--border)] hover:bg-[color:var(--surface)]" data-testid="replay-last">
              <SkipForward size={16} />
            </button>
          </div>
          <input
            type="range"
            min={0}
            max={totalMoves}
            value={idx}
            onChange={(e) => { setPlaying(false); setIdx(Number(e.target.value)); }}
            className="w-full accent-[color:var(--primary)]"
            data-testid="replay-scrubber"
          />
          <div className="mt-2 text-center text-xs text-[color:var(--text-secondary)]" data-testid="replay-position">
            Coup {idx} / {totalMoves}
            {idx > 0 && data.moves[idx - 1] && (
              <span className="ml-2">— <span className={data.moves[idx-1].by === "A" ? "text-stone-200" : "text-stone-400"}>
                {data.moves[idx-1].by === "A" ? "Blancs" : "Noirs"}</span>{" "}
                ({data.moves[idx-1].from.join(",")}) → ({data.moves[idx-1].to.join(",")})
                {data.moves[idx-1].captures.length > 0 && <span className="text-[color:var(--primary)]"> · {data.moves[idx-1].captures.length} prise{data.moves[idx-1].captures.length > 1 ? "s" : ""}</span>}
              </span>
            )}
          </div>
        </div>

        <div className="mt-6 text-center">
          <Link to="/profile" className="text-sm text-[color:var(--text-secondary)] hover:text-[color:var(--text-primary)]" data-testid="replay-back">
            ← Retour au profil
          </Link>
        </div>
      </main>
    </div>
  );
}
